import { useTransition, animated } from '@react-spring/web';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';

import styles from '../../styles/partials/form-status.module.css';

interface FormStatusProps {
    status?: 'loading' | 'success' | 'error' | null;
    successTimeout?: number;
    successMessage?: React.ReactNode;
    iconRight?: boolean;
    className?: string | null;
}

function FormStatus({
    status: providedStatus = null,
    successTimeout = 2000,
    successMessage = null,
    iconRight = false,
    className = null,
}: FormStatusProps) {
    const [status, setStatus] = useState<FormStatusProps['status']>(providedStatus);
    const transitions = useTransition(status !== null ? [status] : [], {
        from: { opacity: 0, scale: 0.5 },
        enter: { opacity: 1, scale: 1 },
        leave: { opacity: 0, scale: 0.5 },
        keys: (item) => item,
    });
    useEffect(() => {
        setStatus(providedStatus);
    }, [providedStatus]);
    useEffect(() => {
        let timeout: ReturnType<typeof setTimeout> | null = null;
        if (status === 'success') {
            timeout = setTimeout(() => {
                setStatus(null);
            }, successTimeout);
        }
        return () => {
            if (timeout !== null) {
                clearTimeout(timeout);
            }
        };
    }, [status, successTimeout]);
    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.iconRight]: iconRight,
                },
                className,
            ])}
        >
            {transitions((style, currentStatus) => {
                let icon = null;
                if (currentStatus === 'loading') {
                    icon = 'progress_activity';
                } else if (currentStatus === 'success') {
                    icon = 'check_circle';
                } else if (currentStatus === 'error') {
                    icon = 'error';
                }
                return (
                    <animated.div style={style} className={styles.status}>
                        {icon !== null ? (
                            <span
                                className={classNames([
                                    'material-symbols-rounded',
                                    styles.icon,
                                    {
                                        [styles.spin]: currentStatus === 'loading',
                                    },
                                ])}
                            >
                                {icon}
                            </span>
                        ) : null}
                        {currentStatus === 'success' && successMessage !== null ? (
                            <div className={styles.message}>{successMessage}</div>
                        ) : null}
                    </animated.div>
                );
            })}
        </div>
    );
}

export default FormStatus;
